import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/LoadingSpinner';

const VideoGeneration = () => {
    const [formData, setFormData] = useState({
        text: '',
        avatar_id: '',
        voice_id: '',
        background_type: 'color',
        background_value: '#ffffff',
        width: 1280,
        height: 720,
        aspect_ratio: '16:9'
    });
    const [loading, setLoading] = useState(false);

    // Fetch avatars
    const { data: avatars, isLoading: avatarsLoading } = useQuery(
        'avatars',
        () => axios.get('/heygen/avatars').then(res => res.data)
    );

    // Fetch voices
    const { data: voices, isLoading: voicesLoading } = useQuery(
        'voices',
        () => axios.get('/heygen/voices').then(res => res.data)
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.text.trim()) {
            toast.error('Please enter text for the video');
            return;
        }

        if (!formData.avatar_id) {
            toast.error('Please select an avatar');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post('/heygen/videos/generate', formData);
            toast.success('Video generation started! Check the Jobs page for progress.');

            // Reset form
            setFormData({
                ...formData,
                text: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to generate video');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Generate AI Video</h1>
                    <p className="text-gray-600">
                        Create professional videos using AI avatars and voices
                    </p>
                </div>

                <div className="bg-white rounded-xl shadow-md border">
                    <div className="p-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Text Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Script Text *
                                </label>
                                <textarea
                                    name="text"
                                    value={formData.text}
                                    onChange={handleInputChange}
                                    required
                                    rows={4}
                                    className="form-input"
                                    placeholder="Enter the text you want your avatar to speak..."
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {formData.text.length} characters
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Avatar Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Avatar *
                                    </label>
                                    {avatarsLoading ? (
                                        <div className="flex items-center justify-center py-4">
                                            <LoadingSpinner />
                                        </div>
                                    ) : (
                                        <select
                                            name="avatar_id"
                                            value={formData.avatar_id}
                                            onChange={handleInputChange}
                                            required
                                            className="form-input"
                                        >
                                            <option value="">Select an avatar</option>
                                            {avatars?.data?.avatars?.map((avatar) => (
                                                <option key={avatar.avatar_id} value={avatar.avatar_id}>
                                                    {avatar.avatar_name} ({avatar.gender})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>

                                {/* Voice Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Voice
                                    </label>
                                    {voicesLoading ? (
                                        <div className="flex items-center justify-center py-4">
                                            <LoadingSpinner />
                                        </div>
                                    ) : (
                                        <select
                                            name="voice_id"
                                            value={formData.voice_id}
                                            onChange={handleInputChange}
                                            className="form-input"
                                        >
                                            <option value="">Select a voice (optional)</option>
                                            {voices?.data?.voices?.map((voice) => (
                                                <option key={voice.voice_id} value={voice.voice_id}>
                                                    {voice.voice_name} ({voice.language} - {voice.gender})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>

                            {/* Background Settings */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Background
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <select
                                        name="background_type"
                                        value={formData.background_type}
                                        onChange={handleInputChange}
                                        className="form-input"
                                    >
                                        <option value="color">Solid Color</option>
                                        <option value="image">Image URL</option>
                                    </select>

                                    {formData.background_type === 'color' ? (
                                        <input
                                            type="color"
                                            name="background_value"
                                            value={formData.background_value}
                                            onChange={handleInputChange}
                                            className="form-input h-10"
                                        />
                                    ) : (
                                        <input
                                            type="url"
                                            name="background_value"
                                            value={formData.background_value}
                                            onChange={handleInputChange}
                                            placeholder="Enter image URL"
                                            className="form-input"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Video Settings */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Video Settings
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Aspect Ratio</label>
                                        <select
                                            name="aspect_ratio"
                                            value={formData.aspect_ratio}
                                            onChange={handleInputChange}
                                            className="form-input"
                                        >
                                            <option value="16:9">16:9 (Landscape)</option>
                                            <option value="9:16">9:16 (Portrait)</option>
                                            <option value="1:1">1:1 (Square)</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Width</label>
                                        <input
                                            type="number"
                                            name="width"
                                            value={formData.width}
                                            onChange={handleInputChange}
                                            min="480"
                                            max="1920"
                                            className="form-input"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Height</label>
                                        <input
                                            type="number"
                                            name="height"
                                            value={formData.height}
                                            onChange={handleInputChange}
                                            min="480"
                                            max="1920"
                                            className="form-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-end pt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn btn-primary btn-lg"
                                >
                                    {loading ? (
                                        <div className="flex items-center">
                                            <LoadingSpinner size="small" className="mr-2" />
                                            Generating Video...
                                        </div>
                                    ) : (
                                        '🎬 Generate Video'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h3 className="font-medium text-blue-900 mb-2">💡 Tips</h3>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Keep scripts concise and natural</li>
                            <li>• Use punctuation for better pacing</li>
                            <li>• Preview avatars before generating</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <h3 className="font-medium text-green-900 mb-2">⚡ Fast Generation</h3>
                        <p className="text-sm text-green-700">
                            Videos typically generate in 2-5 minutes depending on length and complexity.
                        </p>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <h3 className="font-medium text-purple-900 mb-2">📱 Multi-Format</h3>
                        <p className="text-sm text-purple-700">
                            Generate videos in landscape, portrait, or square formats for different platforms.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoGeneration;